import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { CloseRounded } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import colors from "app/styles/colors";
import { ErrorToaster, SuccessToaster } from "../Toaster/Toaster";
import { InputField } from "../UI/TextField";
import { PrimaryButton } from "../Buttons/Button";
import MovingService from "app/apis/Goeasy/Moving/MovingService";

function AddBookingVehicle({ open, handleClose, toggle, handleSubmitFunc }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const [vehicleImage, setVehicleImage] = useState(null);
  const [btnLoader, setBtnLoader] = useState(false);

  const submitVehicleForm = async (formData) => {
    try {
      setBtnLoader(true);
      await MovingService.createVehicle(formData);
      SuccessToaster("Vehicle saved successfully");
      resetFormState();
      handleSubmitFunc();
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setBtnLoader(false);
    }
  };

  const prepareVehicleFormData = (data) => {
    const formData = new FormData();

    Object.entries(data).forEach(([key, value]) => {
      formData.append(key, value);
    });

    if (vehicleImage) {
      formData.append("image", vehicleImage, vehicleImage.name);
    } else {
      ErrorToaster("Vehicle Image is required.");
      return;
    }

    submitVehicleForm(formData);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setVehicleImage(file);
  };

  const resetFormState = () => {
    setVehicleImage(null);
    reset();
  };

  return (
    <Dialog
      open={open}
      fullWidth
      onClose={() => {
        handleClose();
        resetFormState();
      }}
      sx={{ "& .MuiDialog-paper": { borderRadius: 3 } }}
    >
      {/* ================= Title ================= */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontWeight: 600,
        }}
      >
        {toggle ? "Edit Vehicle" : "Add New Vehicle"}
        <IconButton
          onClick={() => {
            handleClose();
            resetFormState();
          }}
        >
          <CloseRounded />
        </IconButton>
      </DialogTitle>

      <Divider />

      {/* ================= Content ================= */}
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit(prepareVehicleFormData)}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <InputField
                label="Vehicle Type"
                error={errors?.type?.message}
                register={register("type", {
                  required: "Vehicle type is required",
                })}
              />
            </Grid>

            <Grid item xs={6}>
              <InputField
                label="Vehicle Name"
                error={errors?.name?.message}
                register={register("name", {
                  required: "Vehicle name is required",
                })}
              />
            </Grid>
            <Grid item xs={6}>
              <InputField
                label="Vehicle Number"
                error={errors?.vehicle_no?.message}
                register={register("vehicle_no", {
                  required: "Vehicle number is required",
                })}
              />
            </Grid>

            <Grid item xs={6}>
              <InputField
                label="Inside Dimension"
                error={errors?.inside_dimension?.message}
                register={register("inside_dimension", {
                  required: "Inside dimension is required",
                })}
              />
            </Grid>

            <Grid item xs={6}>
              <InputField
                label="Door Opening"
                error={errors?.door_opening?.message}
                register={register("door_opening", {
                  required: "Door opening is required",
                })}
              />
            </Grid>

            <Grid item xs={6}>
              <InputField
                label="Deck Height"
                error={errors?.deck_height?.message}
                register={register("deck_height", {
                  required: "Deck height is required",
                })}
              />
            </Grid>

            <Grid item xs={6}>
              <InputField
                type={"number"}
                label="Price"
                error={errors?.price?.message}
                register={register("price", {
                  required: "Price is required",
                  min: {
                    value: 0,
                    message: "Price cannot be less than 0",
                  },
                })}
              />
            </Grid>

            {/* ================= Image Upload ================= */}
            <Grid item xs={12}>
              <Typography>Vehicle Image</Typography>

              <FormControl fullWidth>
                <Box
                  component="label"
                  htmlFor="vehicle-image"
                  sx={{
                    border: `2px dashed ${colors.lightGray}`,
                    borderRadius: 2,
                    height: 200,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    overflow: "hidden",
                    p: 1,
                    mb: 3,
                    "&:hover": {
                      borderColor: colors.primary,
                    },
                  }}
                >
                  {vehicleImage ? (
                    <img
                      src={URL.createObjectURL(vehicleImage)}
                      alt="Vehicle"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Click to upload vehicle image
                    </Typography>
                  )}

                  <input
                    id="vehicle-image"
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </Box>
              </FormControl>
            </Grid>
          </Grid>

          {/* ================= Submit ================= */}
          <PrimaryButton
            loading={btnLoader}
            type="submit"
            title="Save Vehicle"
            fullWidth
            myStyle={{ mt: 2, borderRadius: 2 }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default AddBookingVehicle;
